import storeManager from '../store/StoreManager';
import uiConstants, { BACKGROUND_AUDIO_SUB_HEADER_UI_DATA } from '../constants/UIConstants';
import routeTypes from '../constants/RouteTypes';
import marketPlaces from '../constants/MarketPlaces';
import uiActions from '../actions/UIActions';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import apiReader from '../store/readHelpers/ApiReader';
import sessionConstants from '../constants/SessionConstants';
import marketplaceSettingsService from './MarketplaceSettingsService';
import studentHighlightsService from './StudentHighlightsService';
import utilService from './UtilService';
import i18nService, { t } from './i18nService';
import remoteOralExamService from './RemoteOralExamService';
import tocReader from '../store/readHelpers/TocReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import navigationService from './NavigationService';
import responseReader from '../store/readHelpers/ResponseReader';
import branchRuleReader from '../store/readHelpers/BranchRuleReader';
import marketplacePropertiesService from './MarketplacePropertiesService';
import themeServices from '../themes/ThemeServices';
import cambridgeThemeVariables from '../themes/themeVariables/cambridge';
import cambridgeInnovationThemeVariables from '../themes/themeVariables/cambridgeInnovation';
import fjordThemeVariables from '../themes/themeVariables/fjord';
import ceqThemeVariables from '../themes/themeVariables/ceq';
import defaultThemeVariables from '../themes/themeVariables/default';
import backgroundAudioService from './BackgroundAudioService';
import viewReadyTracker from './ViewReadyTracker';
import { reportError } from './ErrorReporting';
import globalsService from './GlobalsService';
import parentTestService from './ParentTestService';

const uiService = {
    setCurrentViewSize(uiProp) {
        const { DESKTOP, MOBILE, TABLET } = uiConstants.VIEW_SIZE;
        const windowSize = window.innerWidth;
        let viewSize;
        if (windowSize <= uiConstants.BREAKPOINT_MOBILE) {
            viewSize = MOBILE;
        } else if (windowSize > uiConstants.BREAKPOINT_MOBILE && windowSize <= uiConstants.BREAKPOINT_DESKTOP) {
            viewSize = TABLET;
        } else {
            viewSize = DESKTOP;
        }
        if (uiProp.viewSize !== viewSize) {
            storeManager.dispatch(uiActions.uiWindowResized(viewSize));
        }
    },

    getCurrentIOSOrientation() {
        const { LANDSCAPE, PORTRAIT } = uiConstants.ORIENTATION;
        return window.innerWidth > window.innerHeight ? LANDSCAPE : PORTRAIT;
    },

    setScreenIOSOrientation() {
        const orientation = this.getCurrentIOSOrientation();
        storeManager.dispatch(uiActions.uiWindowOrientation(orientation));
    },

    getThemeVariables(mpTheme) {
        switch (mpTheme) {
        case marketPlaces.cambridge:
            return { ...defaultThemeVariables, ...cambridgeThemeVariables };
        case marketPlaces.cambridge_innovation:
            return { ...defaultThemeVariables, ...cambridgeInnovationThemeVariables };
        case marketPlaces.ceq:
            return { ...defaultThemeVariables, ...ceqThemeVariables };
        case marketPlaces.fjord:
            return { ...defaultThemeVariables, ...fjordThemeVariables };
        default:
            return defaultThemeVariables;
        }
    },

    removeThemesFromBody(body) {
        Array
            .from(body.classList)
            .forEach((className) => {
                if (className.indexOf('-theme') > -1) {
                    body.classList.remove(className);
                }
            });
    },

    getDirection(language) {
        return i18nService.RTLDirectionLanguages.includes(language) ? 'rtl' : 'ltr';
    },

    setDirection(language) {
        const direction = this.getDirection(language);
        storeManager.dispatch(uiActions.uiSetDirection(direction));
    },

    setTestPlayerStyle() {
        const body = document.getElementsByTagName('body')[0];
        const testPlayerStyle = settingsReader.getTestPlayerStyle();
        const { ui } = storeManager.getState();

        if (!testPlayerStyle) {
            uiService.setZoomFactor(uiConstants.ZOOM_FACTOR.SMALL);
            this.setTestPlayerTheme();
            return;
        }
        this.removeThemesFromBody(body);
        body.classList.add(`${testPlayerStyle}-theme`);

        const themeName = marketplacePropertiesService.getUiTheme();
        if (themeServices.isThemeOld(themeName)) {
            body.classList.add(`${testPlayerStyle}-old-theme`);
        }

        if (uiConstants.TEST_PLAYER_STYLES.includes(testPlayerStyle)) {
            uiService.setZoomFactor(uiConstants.ZOOM_FACTOR.LARGE);
        } else {
            uiService.setZoomFactor(ui.zoomFactor);
        }
    },

    setTestPlayerTheme() {
        const body = document.getElementsByTagName('body')[0];
        this.removeThemesFromBody(body);

        const marketplaceName = marketplaceSettingsService.getMarketplaceName(window.marketplaceName);
        const themeClassList = this.getThemeClassList(marketplaceName);
        body.classList.add(...themeClassList);
    },

    getThemeClassList(marketplaceName) {
        const classList = [];
        switch (marketplaceName) {
        case marketPlaces.cambridge:
        case marketPlaces.ceq:
        case marketPlaces.cxc:
        case marketPlaces.fjord:
            classList.push(`${marketplaceName}-theme`);
            break;
        case marketPlaces.cambridge_innovation:
            classList.push('fjord-theme');
            classList.push('cambridge-innovation-theme');
            break;
        default:
            classList.push('default-theme');
            break;
        }

        const themeName = marketplacePropertiesService.getUiTheme();
        if (themeServices.isThemeOld(themeName)) {
            classList.push(`${marketplaceName}-old-theme`);
        }

        return classList;
    },

    setZoomFactor(zoomFactor) {
        storeManager.dispatch(uiActions.uiSetZoomFactor(zoomFactor));
    },

    setCurrentMarketplaceTheme() {
        const marketplaceName = marketplaceSettingsService.getMarketplaceName(window.marketplaceName);
        const marketplaceTheme = Object.values(marketPlaces).includes(marketplaceName) ? marketplaceName : 'default';

        storeManager.dispatch(uiActions.uiSetMarketplaceTheme(marketplaceTheme));
        storeManager.dispatch(uiActions.uiSetThemeVariables(
            this.getThemeVariables(marketplaceTheme)
        ));
    },

    readAccessibilityContrastOptions() {
        const a11yContrastOptionString = window.marketplaceProperties.ACCESSIBILITY_CONTRAST_COLORS;
        const contrastOptions = ['normal'];
        if (a11yContrastOptionString?.length > 0) {
            a11yContrastOptionString.split(',').forEach((contrastOption) => {
                contrastOption.trim().toLowerCase();
                if (uiConstants.CONTRAST_OPTIONS.includes(contrastOption)) {
                    contrastOptions.push(contrastOption);
                }
            });
        }

        return contrastOptions;
    },

    shouldUseDarkMode() {
        const { ui } = storeManager.getState();
        return uiConstants.DARK_CONTRAST_COLORS.includes(ui.contrastOption);
    },

    showHeader(routeType, mode, viewMedia, context) {
        return sessionReader.isViewMediaScreen(viewMedia)
            && (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode) || sessionReader.isModeStructure(mode))
            && !sessionReader.isContextViewGradingQuestion(context)
            && (!settingsReader.isPciBasedCustomAssessment())
            && [
                routeTypes.ROUTE_TYPE_ALL,
                routeTypes.ROUTE_TYPE_AUTO_SUBMIT_OFFLINE,
                routeTypes.ROUTE_TYPE_ERROR,
                routeTypes.ROUTE_TYPE_MESSAGES,
                routeTypes.ROUTE_TYPE_OPTIONS,
                routeTypes.ROUTE_TYPE_LOGIN,
            ].indexOf(routeType) === -1;
    },

    showFooter(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment = true) {
        // Don't render the footer when the test is in the pre-start phase
        // or when previewing audio scripts.
        if (sessionReader.isTestThatRequiresPreStartStepAndSittingHasNotYetStarted()
            || context === sessionConstants.CONTEXT_PREVIEW_AUDIO_SCRIPT) {
            return false;
        }
        return (
            !sessionReader.isLayoutParamShowAnswerKeys()
            && !timeHasExpired
            && hasAssessment
            && sessionReader.isViewMediaScreen(viewMedia)
            && (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode) || this.isQuestionWithFooterEnabled(mode)
                || sessionReader.isModeStructure(mode))
            && !sessionReader.isContextViewGradingQuestion(context)
            && !(sessionReader.isRemoteOralExam() && !sessionReader.isContextReadinessCheck())
            && (!settingsReader.isPciBasedCustomAssessment())
            && (
                [
                    '',
                    routeTypes.ROUTE_TYPE_TOC,
                    routeTypes.ROUTE_TYPE_SECTION,
                    routeTypes.ROUTE_TYPE_STIMULUS,
                    routeTypes.ROUTE_TYPE_QUESTION,
                    routeTypes.ROUTE_TYPE_DELIVERING,
                ].indexOf(routeType) !== -1
                || (routeType === routeTypes.ROUTE_TYPE_RESULT && !sessionReader.isContextExam(context))
            )
        );
    },

    isQuestionWithFooterEnabled(mode) {
        return sessionReader.isModeQuestion(mode) && marketplacePropertiesService.isQuestionFooterEnabled();
    },

    showAnnotationsSidebar() {
        return studentHighlightsService.isNoteTakingAndHighlightingEnabled() && sessionReader.isAnnotationsSidebarOpen() && !sessionReader.isPaused();
    },

    showChatSidebar() {
        return settingsReader.isChatEnabled() && sessionReader.isChatOpen();
    },

    showInvisibleNextButton(routeType, mode, viewMedia) {
        return (
            (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode))
            && sessionReader.isViewMediaPdf(viewMedia)
            && [
                routeTypes.ROUTE_TYPE_TOC,
                routeTypes.ROUTE_TYPE_SECTION,
                routeTypes.ROUTE_TYPE_QUESTION,
                routeTypes.ROUTE_TYPE_STIMULUS,
            ].indexOf(routeType) !== -1
        );
    },

    /**
     * Show additional resources task bar only if showing the footer
     * @param routeType
     * @param mode
     * @param viewMedia
     * @param context
     * @param timeHasExpired
     * @returns {boolean}
     */
    showAdditionalResourcesBar(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment) {
        return (
            (
                this.showFooter(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment)
                || routeType === routeTypes.ROUTE_TYPE_TEST_OVERVIEW
                || routeType === routeTypes.ROUTE_TYPE_ROOM_SCAN
            )
            && (sessionReader.questionSetHasAdditionalResources()
                || (settingsReader.isShowRubricForQuestionEnabled()
                    && sessionReader.questionSetHasRubricsMarking()))
            && !(sessionReader.userHasChromeKiosk() && !sessionReader.isInsidePwaChromeApp()) // in chromeApp we add the footer to control the windows
        );
    },

    shouldBlockUnloadWindow() {
        if (utilService.isHeadless()) {
            return false;
        }
        const lock = sessionReader.getNavigationLock();
        return (
            apiReader.hasUnsavedUserResponses()
            || lock.reason === sessionConstants.LOCK.FILE_UPLOAD_IN_PROGRESS
            || lock.reason === sessionConstants.LOCK.DELIVER_IN_PROGRESS
            || lock.reason === sessionConstants.LOCK.EVALUATION_IN_PROGRESS
        );
    },

    showComponentsDoneBox(allChildTestsFinished, timeHasExpired, remoteOralExam) {
        return (sessionReader.isParentTest() && (allChildTestsFinished || timeHasExpired))
            || (sessionReader.isRemoteOralExam() && remoteOralExamService.isTestCompleted(remoteOralExam));
    },

    // TODO: when IA1-6182 is done, need to check if we still need this
    isMathJax3Supported() {
        return !utilService.isSeb() || !utilService.isIOS();
    },

    showRecordingNotification(remoteOralExam) {
        return sessionReader.isRemoteOralExam() && remoteOralExamService.candidateInProgress(remoteOralExam);
    },

    disabledPreviousButton() {
        const { currentlyShown } = storeManager.getState();
        const firstQuestionInfo = tocReader.getFirstQuestionLocation();
        const firstQuestionId = firstQuestionInfo?.contentRevisionId;

        if (currentlyShown.scorableItem) {
            return (currentlyShown.questionId === firstQuestionId
                && !settingsReader.showTableOfContents()
                && currentlyShown.scorableItem === 1)
                || currentlyShown.route === '/';
        }
        return (currentlyShown.questionId === firstQuestionId && !settingsReader.showTableOfContents())
            || currentlyShown.route === '/';
    },

    getRoute() {
        const { currentlyShown } = storeManager.getState();
        return currentlyShown.route;
    },

    disabledNextButton() {
        const { currentlyShown } = storeManager.getState();
        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        // IA1-6496 - if a CAT test and high stakes auto delivery is enabled, next button should not be disabled.
        if (settingsReader.isCatTestType() && navigationService.isOnLastQuestion() && sessionReader.isAutoDeliverHighStakesEnabled()) {
            return false;
        }

        return navigationService.isOnFinalStepPage()
            || (navigationService.isOnLastQuestion() && !navigationService.isFinalStepAvailable() && !sessionReader.isContextReadinessCheck())
            || (settingsReader.hideNextButtonUntilAnswered() && currentlyShown.questionId
                && !navigationService.isOnTOCPage() && !responseReader.isQuestionAttempted(question))
            || (navigationService.isOnLastQuestion() && branchRuleReader.hasBranchRules() && (!apiReader.isOnline() || !apiReader.isServerOK()));
    },

    getElementStyleProperty(element, property) {
        if (!element || !property) {
            return null;
        }

        try {
            const style = window.getComputedStyle(element, null);
            return style.getPropertyValue(property);
        } catch (error) {
            return null;
        }
    },

    shouldUseSimplifiedSummaryPage() {
        return marketplacePropertiesService.isSimplifiedSummaryPageForPublicTestEnabled() && sessionReader.isPublicExam();
    },

    isVerticalLayout(section) {
        if (!section) return false;

        const { VERTICAL, VERTICAL_LEFT, VERTICAL_TOP } = uiConstants.SECTION_LAYOUT;
        return [VERTICAL, VERTICAL_LEFT, VERTICAL_TOP].includes(section.layout);
    },

    isSectionLayoutWithStimulus(section) {
        if (!section) return false;

        let { layout } = section;

        // If no layout & paper submissions hide response areas → resolve layout from nested sections
        if (!layout) {
            const currentQuestion = assessmentReader.getCurrentQuestion();
            const shouldHideResponseAreas =
                settingsReader.shouldHideResponseAreasForPaperSubmissions(currentQuestion) ||
                settingsReader.shouldHideResponseAreaInDemoTestForPaperSubmissions(currentQuestion);

            if (shouldHideResponseAreas) {
                const shownQuestionId = storeManager.getState()?.currentlyShown?.questionId;
                const currentSection = section.sections?.find(eachSection =>
                    eachSection.questions?.some(
                        question => question?.contentRevisionId === shownQuestionId
                    )
                );
                layout = currentSection?.layout;
            }
        }

        const { BASIC_LEFT, VERTICAL_LEFT, VERTICAL_TOP } = uiConstants.SECTION_LAYOUT;
        return [BASIC_LEFT, VERTICAL_LEFT, VERTICAL_TOP].includes(layout);
    },

    isLeftSectionLayout({ layout }) {
        const { BASIC_LEFT, VERTICAL_LEFT } = uiConstants.SECTION_LAYOUT;
        return [BASIC_LEFT, VERTICAL_LEFT].includes(layout);
    },

    isBasicSectionLayout({ layout }) {
        const { BASIC, BASIC_LEFT } = uiConstants.SECTION_LAYOUT;
        return [BASIC, BASIC_LEFT, undefined].includes(layout);
    },

    isTopStimulusPosition(stimulusPosition) {
        return stimulusPosition === uiConstants.STIMULUS_POSITION.TOP;
    },

    isQuestionStimulusHolder(stimulusHolder) {
        return stimulusHolder === uiConstants.STIMULUS_HOLDER.QUESTION;
    },

    scaleImage(imageId, zoomFactor) {
        if (zoomFactor === 1) {
            return;
        }

        const image = document.querySelector(`img[data-content-id="${imageId}"]:not(.scaled)`);
        if (image) {
            const { offsetWidth, offsetHeight } = image;
            image.style.width = `${offsetWidth * zoomFactor}px`;
            image.classList.add('scaled');

            if (offsetHeight > 0 && image.style.height !== 'auto') {
                image.style.height = `${offsetHeight * zoomFactor}px`;
            }
        }
    },

    shouldAddExtraLineSpacing(question) {
        return themeServices.getThemeInstance().shouldAddExtraLineSpacing(question);
    },

    hexToRgba(color) {
        if (!color) return false;

        const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
        return `rgba(${r}, ${g}, ${b}, 0.6)`;
    },

    isInIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    },

    areQuestionSectionsFocusable() {
        return marketplacePropertiesService.getA11yIterationForRegions() > 0;
    },

    getQuestionRegionsAccessibilityAttributes() {
        if (!this.areQuestionSectionsFocusable()) {
            return {};
        }

        return {
            role: 'region',
            tabIndex: 0,
        };
    },

    showBackgroundAudioControlButton() {
        return navigationService.isQuestionPage() && backgroundAudioService.isBackgroundAudioControllable();
    },

    beforeUnloadHandler(e) {
        if (uiService.shouldBlockUnloadWindow()) {
            e.preventDefault();
            e.returnValue = t('navigation.unloadWarning');
        }
    },
    /**
     * Checks if paper-based rendering with multiple stimulus support is enabled.
     * @returns {boolean} True if both print style is paper-based and the marketplace property for multiple stimulus is enabled
     */
    isPbAndMultipleStimulusEnabled() {
        return sessionReader.isPrintStylePaperBased() && marketplacePropertiesService.isMultipleStimuliForPbEnabled();
    },

    isPaperBasedRenderForCEQ() {
        return sessionReader.isPrintStylePaperBased() && themeServices.getThemeInstance().shouldRenderPaperBasedRubric();
    },

    /**
     * setUISettingInLocalStorageForTestTaker(uiOptionKey, optionValue)
     *
     * This function stores a UI setting for the current test-taker in localStorage.
     *
     * This function saves a user-specific UI option (e.g., contrast setting, zoom setting, etc)
     * using a structured object that includes the current assessmentRunId,
     * the test-taker's userId, and the UI option value. Storage is allowed only
     * when the "pause and resume" feature is enabled for test-takers.
     *
     * Structure stored in localStorage:
     * {
     *   assessmentRunId: string | number,
     *   userId: string | number,
     *   [settingData.key]: any   // The option value, or the default fallback
     * }
     *
     * @param {string} uiOptionKey - The identifier for the UI option (must exist in `uiConstants.UI_OPTIONS_DATA`).
     * @param {*} optionValue - The value to store for this option.
     *
     * @returns {void}
     *
     * @usage
     * Call this when updating or persisting a UI preference for a test-taker
     * during an assessment. Example: saving the "contrast" state.
    */

    setUISettingInLocalStorageForTestTaker(uiOptionKey, optionValue) {
        const settingData = uiConstants.UI_OPTIONS_DATA?.[uiOptionKey];

        if (!settingData || !settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            return;
        }

        const assessmentRunId = assessmentReader.getAssessment().assessmentRunId;
        const userId = sessionReader.getUserId();

        const userUIOptionData = JSON.stringify({
            assessmentRunId,
            userId,
            [settingData.key]: optionValue || settingData.defaultValue,
        });
        const userSpecificStorageKey = utilService.getUserSpecificLocalStorageKey(settingData.localStorageKey);
        globalsService.setItemInLocalStorage(
            userSpecificStorageKey,
            userUIOptionData,
        );
    },

    /**
     * getUISettingFromLocalStorageForTestTaker(uiOptionKey)
     *
     * This function retrieves a UI setting for the current test-taker from localStorage.
     *
     * This function reads a previously stored UI option for the user and
     * validates that it belongs to the current assessment and user (matching assessmentRunId
     * and userId). If the stored data is invalid, missing, or belongs to a different
     * assessment/user, it returns the default value. It may also remove outdated
     * per-user settings when assessmentRunId changes.
     *
     * Structure expected in localStorage:
     * {
     *   assessmentRunId: string | number,
     *   userId: string | number,
     *   [settingData.key]: any   // The stored option value
     * }
     *
     * @param {string} uiOptionKey - The identifier for the UI option (must exist in `uiConstants.UI_OPTIONS_DATA`).
     *
     * @returns {*} The stored option value if valid, otherwise the default value.
     *
     * @usage
     * Call this when loading UI preferences for a test-taker at the start
     * of an assessment or when restoring UI state (e.g., restoring the contrast option preference for the test taker).
    */

    getUISettingFromLocalStorageForTestTaker(uiOptionKey) {
        const settingData = uiConstants.UI_OPTIONS_DATA?.[uiOptionKey];

        if (!settingData) {
            return null;
        }
        if (!settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            return settingData.defaultValue;
        }
        const userSpecificStorageKey = utilService.getUserSpecificLocalStorageKey(settingData.localStorageKey);
        const localStorageValue = globalsService.getItemFromLocalStorage(userSpecificStorageKey);
        if (!localStorageValue) {
            return settingData.defaultValue;
        }
        try {
            const parsedValue = JSON.parse(localStorageValue) || {};
            const { assessmentRunId, userId } = parsedValue;
            const currentTestAssessmentRunId = parentTestService.getAvailableAssessmentRunId();

            if (assessmentRunId === currentTestAssessmentRunId && userId === sessionReader.getUserId()) {
                return parsedValue?.[settingData.key] || settingData.defaultValue;
            }
            // if not the previous test setting for same user, then clear local storage for this setting
            // for that user
            if (userId === sessionReader.getUserId() && assessmentRunId !== currentTestAssessmentRunId) {
                globalsService.removeItemFromLocalStorage(userSpecificStorageKey);
            }
        } catch (error) {
            viewReadyTracker.logError();
            reportError(new Error(
                `Failed to parse UI setting from localStorage for key "${userSpecificStorageKey}": ${error?.message ? error.message : error}`
            ));
        }
        return settingData.defaultValue;
    },

    /**
     * Returns UI metadata for the background-audio sub-header based on the given audio status.
     *
     * Looks up the configuration in `BACKGROUND_AUDIO_SUB_HEADER_UI_DATA` using the
     * provided `audioStatus` key and returns the matching UI data object (if any).
     *
     * @param {string} audioStatus - Current background audio status (e.g., "PLAYING", "PAUSED", "ENDED").
     * @returns {Object|undefined} The UI data associated with the given status, or `undefined`
     * if no matching entry exists.
    */
    getUIHeaderDataForBackgroundAudio(audioStatus) {
        return BACKGROUND_AUDIO_SUB_HEADER_UI_DATA?.[audioStatus];
    },

    /* Applies image filter for paper-based rendering based on marketplace properties.
     *  For fjord theme with PDF_FOR_IOP enabled: applies grayscale filter.
     *  For CEQ with PB_RENDER_STYLE='ceq': applies grayscale with contrast/brightness adjustment.
    */
    applyImageFilterForPaperBased() {
        if (marketplacePropertiesService.isPdfForIopEnabled() && document.body.classList.contains('fjord-theme')) {
            document.body.setAttribute('enable-gray-scale', 'true');
        } else if (settingsReader.isPBRenderForCeqEnabled()) {
            document.body.setAttribute('enable-ceq-paper-based-filter', 'true');
        }
    },
};

export default uiService;
